import React, {
  createContext, useContext, useMemo, useEffect,
} from 'react';
import PropTypes from 'prop-types';

/*
 * AppThemeContext provides theming and configuration for all application pages.
 * It supports light and dark mode, and exposes a single config structure for both.
 * This is configured in Cosmic.js, but default are provided below.
 * To use: import { useAppTheme } from '@Components/Context';
 * Then: const { theme, isDarkMode, setDarkMode, ... } = useAppTheme();
 * Notes:
 * - 'theme' contains the current light/dark theme's colors and assets
 * - 'isDarkMode' is a boolean indicating if dark mode is active
 * - 'setDarkMode' is a function to toggle dark mode
 * - Additional config values (e.g. icons) are also exposed for use across the app
 * - All config values can be seen in the defaultThemeConfig below.
 * - Sets CSS variables on the root element for use in stylesheets (e.g. var(--nav-background))
 */

const LOCAL_STORAGE_KEY = 'appThemePreference';
// Default theme config with both light and dark themes
const defaultThemeConfig = {
  lightTheme: {
    navigation: {
      background: '#FFFFFF',
      bannerBg: 'https://imgix.cosmicjs.com/142f75b0-08a7-11ef-9eca-7d347081a9fb-blue-abstract-bg.svg?q=75&auto=format,compress',
      bannerBgColor: '#5766F8',
      bannerTextColor: '#FFFFFF',
      border: '#D8D8D8',
    },
    page: {
      background: '#FFFFFF',
      altBg: '#F2F2F2',
      cardBg: '#FFFFFF',
      highlight: '#004FFE',
      altHighlight: '#038BFC',
      primaryText: '#3C3C3C',
      secondaryText: '#353E43',
      hintText: '#9C9C9C',
      numberHighlight: '#000B82',
      urlColor: '#252C32',
      incrementText: '#299914',
      decrementText: '#FF7E1C',
      tableBg: '#FFFFFF',
      tableText: '#3C3C3C',
      tableBorder: '#E5E9EB',
      tableHeader: '#84919A',
      inputBg: '#FFFFFF',
      inputText: '#3C3C3C',
      inputIcon: '#585858',
      inputBorder: '#D8D8D8',
      focusHighlight: '#038BFC',
      modalBg: '#FFFFFF',
      modalText: '#3C3C3C',
      statusSuccess: '#1B5E20',
      statusError: '#E53E3E',
    },
    button: {
      primaryBg: '#004FFE',
      primaryText: '#FFFFFF',
      hoverBg: '#D7EDFF',
      hoverText: '#038BFC',
      selectBg: '#F0F6FF',
      tableBg: '#F2F2F2',
      tableText: '#585858',
      tableIcon: '#585858',
      border: '#D8D8D8',
      disabledBg: '#EFEFEF',
      disabledText: '#A2A2A2',
      iconColor: '#9C9C9C',
      iconHoverColor: '#038BFC',
      iconDisabledColor: '#626970',
    },
  },
  darkTheme: {
    navigation: {
      background: '#060317',
      bannerBg: 'https://imgix.cosmicjs.com/7270f470-1686-11f1-b798-bdb9e44e35fe-abstract-radar-banner-bg-min.png?q=75&auto=format,compress',
      bannerBgColor: '#060317',
      bannerTextColor: '#FFFFFF',
      border: '#2F3B49',
    },
    page: {
      background: '#060317',
      altBg: '#14191F',
      cardBg: '#191F29',
      highlight: '#F94B4B',
      altHighlight: '#fE6464',
      primaryText: '#FFFFFF',
      secondaryText: '#FE6464',
      hintText: '#FE6464',
      numberHighlight: '#FE6464',
      urlColor: '#FFFFFF',
      incrementText: '#62C150',
      decrementText: '#EC2C2C',
      tableBg: '#050316',
      tableText: '#FFFFFF',
      tableBorder: '#E5E9EB',
      tableHeader: '#FE6464',
      inputBg: '#2A2929',
      inputText: '#FFFFFF',
      inputIcon: '#FE6464',
      inputBorder: '#394658',
      focusHighlight: '#F94B4B',
      modalBg: '#14191F',
      modalText: '#FFFFFF',
      statusSuccess: '#62C150',
      statusError: '#F94B4B',
    },
    button: {
      primaryBg: '#F94B4B',
      primaryText: '#FFFFFF',
      hoverBg: '#F94B4B',
      hoverText: '#FFFFFF',
      selectBg: '#454A59',
      tableBg: '#2A2929',
      tableText: '#FFFFFF',
      tableIcon: '#FE6464',
      border: '#2F3B49',
      disabledBg: '#EFEFEF',
      disabledText: '#A2A2A2',
      iconColor: '#FFFFFF',
      iconHoverColor: '#FFFFFF',
      iconDisabledColor: '#626970',
    },
  },
  // Shared config (set darkBg to indicate if noAuthPages should use dark theme colors)
  noAuthPages: {
    darkBg: true,
    bgImage: 'https://imgix.cosmicjs.com/172399c0-12ac-11f1-9e28-d5fea3b8af7e-abstract-radar-bg-min.png?q=85&auto=format,compress',
    background: '#060317',
  },
  statusColorMap: {
    running: { text: '#22684B', background: '#EDFDF5' },
    completed: { text: '#3A5985', background: '#EFF8FF' },
    paused: { text: '#AB5741', background: '#FCF8EF' },
    scheduled: { text: '#7364A7', background: '#FAF6FF' },
    failed: { text: '#DB0000', background: '#FFF5EE' },
  },
  icons: {
    piracyFlagIcon: 'https://imgix.cosmicjs.com/567df730-069e-11ef-9eca-7d347081a9fb-piracy-flag-icon.svg?q=85&auto=format,compress',
    sentimentHeartIcon: 'https://imgix.cosmicjs.com/568ac870-069e-11ef-9eca-7d347081a9fb-sentiment-heart-icon.svg?q=85&auto=format,compress',
    allSearchesIcon: 'https://imgix.cosmicjs.com/48884450-12e7-11ef-9eca-7d347081a9fb-all-searches-icon.svg?q=85&auto=format,compress',
    liveSearchesIcon: 'https://imgix.cosmicjs.com/1eef0160-12e7-11ef-9eca-7d347081a9fb-live-searches-icon.svg?q=85&auto=format,compress',
    completedSearchesIcon: 'https://imgix.cosmicjs.com/568b3da0-069e-11ef-9eca-7d347081a9fb-completed-searches-icon.svg?q=85&auto=format,compress',
    pausedSearchesIcon: 'https://imgix.cosmicjs.com/564d9860-069e-11ef-9eca-7d347081a9fb-paused-searches-icon.svg?q=85&auto=format,compress',
    calendarInputIcon: 'https://imgix.cosmicjs.com/bbbbc9f0-03f2-11ef-bec5-7f9403a32f29-calendar-input-icon.svg?q=85&auto=format,compress',
    usageIcon: 'https://imgix.cosmicjs.com/951946e0-03f4-11ef-bec5-7f9403a32f29-usage-icon.svg?q=85&auto=format,compress',
    seeDetailsIcon: 'https://imgix.cosmicjs.com/68ee7140-1316-11ef-9eca-7d347081a9fb-see-details-arrow-icon.svg?q=85&auto=format,compress',
    signInIconHighlight: 'https://imgix.cosmicjs.com/8cc2aa20-13b9-11ef-9eca-7d347081a9fb-sign-in-icon-highlight.svg?q=85&auto=format,compress',
    timerClockIcon: 'https://imgix.cosmicjs.com/ea9709b0-1897-11ef-9eca-7d347081a9fb-timer-clock-icon.svg?q=85&auto=format,compress',
    customReportsIcon: 'https://imgix.cosmicjs.com/aa4ce4b0-7c3d-11ef-915f-61acd7484848-customReportsIcon.svg?q=85&auto=format,compress',
    settingsIcon: 'https://imgix.cosmicjs.com/317d3070-5f38-11ef-b5ae-a594bb4a8e67-settings-icon.svg?q=85&auto=format,compress',
    searchPriorityIcon: 'https://imgix.cosmicjs.com/2f2dcba0-65ae-11ef-b5ae-a594bb4a8e67-priority-arrow-up-icon.svg?q=85&auto=format,compress',
    denyListIcon: 'https://imgix.cosmicjs.com/0ffd75e0-bd7f-11ef-bee4-3bb1d3c55332-deny-list-icon.svg?q=85&auto=format,compress',
    allowListIcon: 'https://imgix.cosmicjs.com/5b76e6f0-bd7f-11ef-bee4-3bb1d3c55332-allow-list-icon.svg?q=85&auto=format,compress',
    authorAllowIcon: 'https://imgix.cosmicjs.com/aab6bf30-0b39-11f0-a78b-ab260f4a128e-allow-authors-icon.svg?q=85&auto=format,compress',
    reviewDenyIcon: 'https://imgix.cosmicjs.com/317d3070-5f38-11ef-b5ae-a594bb4a8e67-settings-icon.svg?q=85&auto=format,compress',
    reviewDenyPlatformIcon: 'https://imgix.cosmicjs.com/317d3070-5f38-11ef-b5ae-a594bb4a8e67-settings-icon.svg?q=85&auto=format,compress',
    companyUnitsIcon: 'https://imgix.cosmicjs.com/ce3c61c0-8510-11f0-986f-9bf1fe5c24aa-company-units-icon.svg?q=85&auto=format,compress',
    listAssetsIcon: 'https://imgix.cosmicjs.com/7c04dbd0-1138-11f1-bd1a-5344fff0f60b-cid-assets-list-icon.svg?q=85&auto=format,compress',
    listClaimsIcon: 'https://imgix.cosmicjs.com/7c05ed40-1138-11f1-bd1a-5344fff0f60b-cid-claims-icon.svg?q=85&auto=format,compress',
    claimsUploadIcon: 'https://imgix.cosmicjs.com/12b40490-1141-11f1-bd1a-5344fff0f60b-claims-upload-icon.svg?q=85&auto=format,compress',
    cyclopsAnalyticsIcon: 'https://imgix.cosmicjs.com/95c655e0-7c3c-11ef-915f-61acd7484848-cyclopsAnalyticsIcon.svg?q=85&auto=format,compress',
    cyclopsLiveEventsIcon: 'https://imgix.cosmicjs.com/95c80390-7c3c-11ef-915f-61acd7484848-cyclopsLiveEventsIcon.svg?q=85&auto=format,compress',
    cyclopsPastEventsIcon: 'https://imgix.cosmicjs.com/95eddb10-7c3c-11ef-915f-61acd7484848-cyclopsPastEventsIcon.svg?q=85&auto=format,compress',
    searchesIcon: 'https://imgix.cosmicjs.com/408b9500-2221-11f0-adb4-416e2c304a45-allSearchesIconNew.svg?q=85&auto=format,compress',
    confirmedRemovalsIcon: 'https://imgix.cosmicjs.com/4bd66270-2229-11f0-adb4-416e2c304a45-confirmedRemovalsIcon.svg?q=85&auto=format,compress',
    allInfringementsIcon: 'https://imgix.cosmicjs.com/98f78020-2486-11f0-a845-63684478fa01-allInfringementsIcon.svg?q=85&auto=format,compress',
    fingerprintingSettingsIcon: 'https://imgix.cosmicjs.com/317d3070-5f38-11ef-b5ae-a594bb4a8e67-settings-icon.svg?q=85&auto=format,compress',
    liveTrackerIcon: 'https://imgix.cosmicjs.com/95c80390-7c3c-11ef-915f-61acd7484848-cyclopsLiveEventsIcon.svg?q=85&auto=format,compress',
    creatorReferralsIcon: 'https://imgix.cosmicjs.com/58e0bc50-458b-11f1-a3ff-65bbafb72c6d-creator-referrals-icon-v1.svg?q=85&auto=format,compress',
    ispAllowlistIcon: 'https://imgix.cosmicjs.com/4c1f01e0-4fed-11f1-8004-49554e815733-isp-allowlist-icon.svg?q=85&auto=format,compress',
    youtubeIcon: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'/%3E%3C/svg%3E",
  },
  constructionImage: 'https://imgix.cosmicjs.com/6e7c5410-1e0b-11ef-9eca-7d347081a9fb-under-construction-signs.png?q=75&auto=format,compress',
  accountPlaceholder: 'https://imgix.cosmicjs.com/e7822150-6a14-11ef-b5ae-a594bb4a8e67-account-placeholder.png?q=75&auto=format,compress',
  settingsDisplayMap: {
    'reviewable-result-notification-access': 'Notify me of newly found results that can be reviewed with the review tool',
  },
  settingsMenuConfig: {
    'dmca-denylist': {
      label: 'DMCA denylist',
      hint: 'The domain denylist includes piracy domains to send DMCA notices to when infringements are detected that match any domain on this list.',
    },
    'dmca-allowlist': {
      label: 'DMCA allowlist',
      hint: 'DMCA notices will not be sent for any results with domains on the allowlist.',
    },
    'dmca-author_allowlist': {
      label: 'DMCA profile allowlist',
      pageTitle: 'DMCA Profile Allowlist',
      actionTerm: 'profile',
      hint: 'DMCA notices will not be sent for any results with a profile or channel on the allowlist.',
    },
    'dmca-isp_allowlist': {
      label: 'DMCA ISP allowlist',
      pageTitle: 'DMCA ISP Allowlist',
      actionTerm: 'ISP',
      hint: 'DMCA notices will not be sent for any domains whose ISP is in the ISP allowlist.',
    },
    'review-denylist': {
      label: 'Review denylist',
      hint: 'Infringements with domains matching any on this list will always be present in the Review tool.',
    },
    'review-deny_platforms': {
      label: 'Review deny platforms',
      hint: 'Infringements found on platforms matching any on this list will always be present in the Review tool.',
    },
  },
  cyclopsStatusDisplayMap: {},
  hideBlacklightSearchCompanies: [],
  disableSearchFeatures: false,
  disableReviewFeatures: false,
  disableResultsFeatures: false,
  showStatusMessage: false,
};

function setThemeCSSVariables(theme, sharedConfig) {
  try {
    const root = document.documentElement;
    // Navigation e.g. var(--nav-background)
    Object.entries(theme.navigation).forEach(([key, value]) => {
      root.style.setProperty(
        `--nav-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`,
        value,
      );
    });
    // Page e.g. var(--page-primary-text)
    Object.entries(theme.page).forEach(([key, value]) => {
      root.style.setProperty(
        `--page-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`,
        value,
      );
    });
    // Button e.g. var(--button-primary-bg)
    Object.entries(theme.button).forEach(([key, value]) => {
      root.style.setProperty(
        `--button-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`,
        value,
      );
    });
    // noAuthPages e.g. var(--noauth-background)
    if (sharedConfig.noAuthPages) {
      Object.entries(sharedConfig.noAuthPages).forEach(([key, value]) => {
        if (typeof value === 'string') {
          root.style.setProperty(
            `--noauth-${key.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`)}`,
            value,
          );
        }
      });
    }
    // Elevations and other shared variables
    root.style.setProperty('--elevation', '0px 2px 7px 0px rgba(0, 0, 0, 0.12)');
    root.style.setProperty('--elevation-hover', '0px 4px 12px 0px rgba(0, 0, 0, 0.16)');
    root.style.setProperty('--elevation-active', '0 10px 22px rgba(9, 45, 35, 0.10)');
    root.style.setProperty('--elevation-value', '0px 2px 7px 0px');
    root.style.setProperty('--elevation-hover-value', '0px 4px 12px 0px');
    root.style.setProperty('--elevation-active-value', '0 10px 22px');
    root.style.setProperty('--table-controls-height', '1.85rem');
    root.style.setProperty('--app-radius', '0.35rem');
    root.style.setProperty('--app-warning', '#FF7E1C');
    root.style.setProperty('--app-error', '#E53E3E');
    root.style.setProperty('--app-success', '#299914');
    root.style.setProperty('--app-success-dark', '#22684B');
  } catch (err) {
    // Log error for debugging
    /* eslint-disable-next-line no-console */
    console.error('Error setting theme CSS variables:', err);
  }
}

// Utility to deeply merge theme config with defaults, filling missing keys
function mergeThemeConfigWithDefaults(config, defaults) {
  if (!config || typeof config !== 'object') return defaults;
  const result = Array.isArray(defaults) ? [] : {};
  Object.keys(defaults).forEach((key) => {
    if (
      typeof defaults[key] === 'object'
      && defaults[key] !== null
      && !Array.isArray(defaults[key])
    ) {
      result[key] = mergeThemeConfigWithDefaults(config[key], defaults[key]);
    } else {
      result[key] = config[key] !== undefined ? config[key] : defaults[key];
    }
  });
  // Add any extra keys from config not in defaults
  Object.keys(config).forEach((key) => {
    if (result[key] === undefined) {
      result[key] = config[key];
    }
  });
  return result;
}

// Utility to validate theme config structure
function isValidThemeConfig(config) {
  if (!config || typeof config !== 'object') return false;
  const requiredKeys = ['lightTheme', 'darkTheme'];
  Object.keys(requiredKeys).forEach((key) => {
    if (
      !config[key]
      || typeof config[key] !== 'object'
      || !config[key].navigation
      || !config[key].page
      || !config[key].button
    ) {
      return false;
    }

    return true;
  });

  const sharedKeys = ['icons', 'noAuthPages', 'statusColorMap'];
  Object.keys(sharedKeys).forEach((key) => {
    if (config[key] && typeof config[key] !== 'object') {
      return false;
    }
    return true;
  });
  return true;
}

const AppThemeContext = createContext({
  themeConfig: defaultThemeConfig,
  isDarkMode: false,
  setDarkMode: () => {},
});

export const useAppTheme = () => useContext(AppThemeContext);

export const AppThemeProvider = ({
  children,
  themeConfig,
  isDarkMode: isDarkModeProp = true,
  setDarkMode: setDarkModeProp,
  themeState = null,
}) => {
  // Read persisted preference from localStorage (fallback to prop or false)
  const getInitialDarkMode = () => {
    // 1. Redux state (DB value)
    if (themeState === 'dark') return true;
    if (themeState === 'light') return false;
    // 2. localStorage
    const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (stored === 'true') return true;
    if (stored === 'false') return false;
    // 3. Prop or default
    return isDarkModeProp;
  };

  const [isDarkMode, setDarkMode] = React.useState(getInitialDarkMode);

  // Persist preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(LOCAL_STORAGE_KEY, isDarkMode ? 'true' : 'false');
  }, [isDarkMode]);

  // Sync with Redux state (DB value) if it changes
  useEffect(() => {
    if (themeState === 'dark' && !isDarkMode) setDarkMode(true);
    if (themeState === 'light' && isDarkMode) setDarkMode(false);
    // If null, do nothing (use localStorage/default)
  }, [themeState]);

  // Allow external control if setDarkModeProp is provided
  const setDarkModeHandler = setDarkModeProp || setDarkMode;

  // Merge themeConfig with defaults, filling missing keys
  const mergedThemeConfig = useMemo(() => {
    if (!themeConfig || !isValidThemeConfig(themeConfig)) {
      return defaultThemeConfig;
    }
    return mergeThemeConfigWithDefaults(themeConfig, defaultThemeConfig);
  }, [themeConfig]);

  const theme = useMemo(() => (
    isDarkMode ? mergedThemeConfig.darkTheme : mergedThemeConfig.lightTheme
  ), [mergedThemeConfig, isDarkMode]);

  const sharedConfig = useMemo(() => {
    const { lightTheme, darkTheme, ...shared } = mergedThemeConfig;
    return shared;
  }, [mergedThemeConfig]);

  useEffect(() => {
    setThemeCSSVariables(theme, sharedConfig);

    // Ensure variables are set after a short delay (handles async errors)
    const retryTimeout = setTimeout(() => {
      setThemeCSSVariables(theme, sharedConfig);
    }, 100);

    return () => clearTimeout(retryTimeout);
  }, [theme, sharedConfig]);

  const value = useMemo(
    () => ({
      themeConfig: mergedThemeConfig,
      theme,
      isDarkMode,
      setDarkMode: setDarkModeHandler,
      ...sharedConfig,
    }),
    [themeConfig, theme, isDarkMode, setDarkModeHandler, sharedConfig],
  );

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
};

AppThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  themeConfig: PropTypes.object,
  isDarkMode: PropTypes.bool,
  setDarkMode: PropTypes.func,
  themeState: PropTypes.string,
};
